export const ROUTE_CACHE_TIME = 24 * 60 * 60 * 1000
export const ROUTE_CACHE_LIMIT = 1000

const DATABASE_NAME = 'econtainers-valhalla-routes-v1'
const STORE_NAME = 'routes'
let databasePromise = null

const getDatabase = () => {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (databasePromise) return databasePromise

  databasePromise = new Promise(resolve => {
    let settled = false
    const finish = database => {
      if (settled) {
        if (database) database.close()
        return
      }
      settled = true
      resolve(database)
    }
    try {
      if (!window.indexedDB) {
        finish(null)
        return
      }
      const request = window.indexedDB.open(DATABASE_NAME, 1)
      request.onupgradeneeded = () => {
        const store = request.result.createObjectStore(STORE_NAME, {
          keyPath: 'key'
        })
        store.createIndex('expiresAt', 'expiresAt')
        store.createIndex('lastUsedAt', 'lastUsedAt')
      }
      request.onsuccess = () => {
        const database = request.result
        database.onversionchange = () => {
          database.close()
          databasePromise = null
        }
        finish(database)
      }
      request.onerror = () => finish(null)
      request.onblocked = () => finish(null)
    } catch (error) {
      finish(null)
    }
  })
  return databasePromise
}

export const isCacheableRoute = (route, profile = 'primary') =>
  profile === 'summary'
    ? Number.isFinite(route?.distanceKm) && route.distanceKm >= 0
    : Array.isArray(route?.coordinates) &&
      route.coordinates.length > 1 &&
      route.coordinates.every(
        coordinate =>
          Array.isArray(coordinate) &&
          coordinate.length === 2 &&
          coordinate.every(Number.isFinite)
      )

export const readRouteCache = async (key, profile = 'primary') => {
  const database = await getDatabase()
  if (!database) return null

  return new Promise(resolve => {
    try {
      const transaction = database.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(key)
      let cached = null
      transaction.oncomplete = () => resolve(cached)
      transaction.onerror = () => resolve(null)
      transaction.onabort = () => resolve(null)
      request.onsuccess = () => {
        const entry = request.result
        const now = Date.now()
        if (
          entry &&
          Number.isFinite(entry.expiresAt) &&
          entry.expiresAt > now &&
          isCacheableRoute(entry.route, profile)
        ) {
          cached = { route: entry.route, expiresAt: entry.expiresAt }
          store.put({ ...entry, lastUsedAt: now })
        } else if (entry) {
          store.delete(key)
        }
      }
    } catch (error) {
      resolve(null)
    }
  })
}

const evictOldestRoutes = store => {
  const countRequest = store.count()
  countRequest.onsuccess = () => {
    let remaining = countRequest.result - ROUTE_CACHE_LIMIT
    if (remaining <= 0) return
    const cursorRequest = store.index('lastUsedAt').openCursor()
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result
      if (!cursor || remaining <= 0) return
      cursor.delete()
      remaining -= 1
      cursor.continue()
    }
  }
}

const cleanExpiredRoutes = (store, now) => {
  const request = store.index('expiresAt').openCursor()
  request.onsuccess = () => {
    const cursor = request.result
    if (cursor && cursor.value.expiresAt <= now) {
      cursor.delete()
      cursor.continue()
    } else {
      evictOldestRoutes(store)
    }
  }
}

export const writeRouteCache = async (
  key,
  route,
  expiresAt,
  profile = 'primary'
) => {
  if (!isCacheableRoute(route, profile)) return false
  const database = await getDatabase()
  if (!database) return false

  return new Promise(resolve => {
    try {
      const now = Date.now()
      const transaction = database.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      transaction.oncomplete = () => resolve(true)
      transaction.onerror = () => resolve(false)
      transaction.onabort = () => resolve(false)
      store.put({ key, route, expiresAt, lastUsedAt: now })
      cleanExpiredRoutes(store, now)
    } catch (error) {
      resolve(false)
    }
  })
}
